import { DateTime } from 'luxon';

import type { DaySegmentStatus, DayStatusSegment } from '../types';
import type { PositionedEvent } from '../types';
import { CALENDAR_VIEW_CONSTANTS } from '../types';

const MINUTES_PER_DAY = 24 * 60;
const { STATUS_BAR_RESOLUTION_MINUTES } = CALENDAR_VIEW_CONSTANTS;

/** Преобразует ISO дату-время в минуты от полуночи данного дня (0..MINUTES_PER_DAY), с ограничением по границам дня */
function toMinutesInDay(iso: string, dayStart: DateTime): number {
  const dt = DateTime.fromISO(iso);

  if (!dt.isValid) return 0;

  const diff = dt.diff(dayStart, 'minutes').minutes;

  return Math.max(0, Math.min(Math.floor(diff), MINUTES_PER_DAY));
}

/** Собирает залогированные интервалы времени из событий (ворклоги: другие логи, matchedWorklog, логи из свёрнутой группы) */
function getLoggedIntervals(
  dayStart: DateTime,
  positionedEvents: PositionedEvent[],
): Array<[number, number]> {
  const intervals: Array<[number, number]> = [];

  for (const { event } of positionedEvents) {
    // Другой лог (одиночный): весь интервал события считается залогированным
    if (event.isOtherLog && event.otherLogData) {
      const startMin = toMinutesInDay(event.startsAt, dayStart);
      const endMin = toMinutesInDay(event.endsAt, dayStart);

      if (endMin > startMin) intervals.push([startMin, endMin]);
    }

    // Свёрнутая группа других логов: каждый лог в группе залогирован
    if (event.isOtherLog && event.collapsedGroup) {
      for (const log of event.collapsedGroup.logs) {
        const startMin = toMinutesInDay(log.start, dayStart);
        const endMin = Math.min(startMin + log.durationMinutes, MINUTES_PER_DAY);

        if (endMin > startMin) intervals.push([startMin, endMin]);
      }
    }

    // Событие CalDAV с привязанным ворклогом: залогированная часть
    if (
      !event.isOtherLog &&
      !event.isDraft &&
      event.matchedWorklog &&
      event.matchedWorklog.duration > 0
    ) {
      const wl = event.matchedWorklog;
      const startMin = wl.start
        ? toMinutesInDay(wl.start, dayStart)
        : toMinutesInDay(event.startsAt, dayStart);
      const endMin = Math.min(startMin + wl.duration, MINUTES_PER_DAY);

      if (endMin > startMin) intervals.push([startMin, endMin]);
    }
  }

  return intervals;
}

/** Собирает интервалы встреч (только события CalDAV; другие логи и черновики не считаются встречами) */
function getMeetingIntervals(
  dayStart: DateTime,
  positionedEvents: PositionedEvent[],
): Array<[number, number]> {
  const intervals: Array<[number, number]> = [];

  for (const { event } of positionedEvents) {
    if (event.isOtherLog || event.isDraft) continue;

    const startMin = toMinutesInDay(event.startsAt, dayStart);
    const endMin = toMinutesInDay(event.endsAt, dayStart);

    if (endMin > startMin) intervals.push([startMin, endMin]);
  }

  return intervals;
}

function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** Объединяет подряд идущие сегменты с одинаковым статусом */
function mergeSegments(
  segments: Array<{ startMinutes: number; endMinutes: number; status: DaySegmentStatus }>,
): DayStatusSegment[] {
  if (segments.length === 0) return [];

  const result: DayStatusSegment[] = [];
  let current = { ...segments[0] };

  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i];

    if (seg.status === current.status && seg.startMinutes === current.endMinutes) {
      current.endMinutes = seg.endMinutes;
    } else {
      result.push(current);
      current = { ...seg };
    }
  }

  result.push(current);

  return result;
}

/**
 * Вычисляет интервалы статуса для полосы статуса колонки дня.
 * Возвращает сегменты (объединённые при одинаковом соседнем статусе) со статусами:
 * - free: нет встречи, нейтрально
 * - logged: время покрыто ворклогом
 * - unlogged_meeting: время встречи CalDAV не (полностью) покрыто ворклогом
 */
export function getDayStatusIntervals(
  dayDate: DateTime,
  timedEvents: PositionedEvent[],
): DayStatusSegment[] {
  const dayStart = dayDate.startOf('day');

  const loggedRanges = getLoggedIntervals(dayStart, timedEvents);
  const meetingRanges = getMeetingIntervals(dayStart, timedEvents);

  const numSegments = Math.ceil(MINUTES_PER_DAY / STATUS_BAR_RESOLUTION_MINUTES);
  const segments: Array<{ startMinutes: number; endMinutes: number; status: DaySegmentStatus }> =
    [];

  for (let i = 0; i < numSegments; i++) {
    const startMinutes = i * STATUS_BAR_RESOLUTION_MINUTES;
    const endMinutes = Math.min(startMinutes + STATUS_BAR_RESOLUTION_MINUTES, MINUTES_PER_DAY);
    let status: DaySegmentStatus = 'free';

    const hasLogged = loggedRanges.some(([s, e]) => overlaps(startMinutes, endMinutes, s, e));

    if (hasLogged) {
      status = 'logged';
    } else {
      const hasMeeting = meetingRanges.some(([s, e]) => overlaps(startMinutes, endMinutes, s, e));

      if (hasMeeting) status = 'unlogged_meeting';
    }

    segments.push({ startMinutes, endMinutes, status });
  }

  return mergeSegments(segments);
}
